import { z } from "zod";
import { FACT_TEXT_GUIDANCE, FACT_TEXT_MAX_LENGTH, FactTopicSchema } from "./company-facts.js";
import { Id, IsoDate } from "./ids.js";

/**
 * Automatic memory capture: after a run finishes, a background pass reads the chat's new messages
 * and saves what should outlast them (company facts, decisions, tasks and deliverables) without
 * asking. These are the extraction's structured output and the owner's view of what it saved.
 */
export const AUTO_ITEM_KINDS = ["fact", "decision", "task", "deliverable"] as const;
export const AutoItemKindSchema = z.enum(AUTO_ITEM_KINDS);
export type AutoItemKind = z.infer<typeof AutoItemKindSchema>;

export const CAPTURE_ITEMS_MAX = 20;

const line = (max: number) => z.string().trim().min(1).max(max);

/** One item the extraction found, tied to the message it came from. */
export const CapturedItemSchema = z.object({
  kind: AutoItemKindSchema,
  /**
   * What the item is about, short and stable across wordings ("brand primary colour", "launch
   * date", "Acme monthly price"), so a later value for the same subject updates it.
   */
  key: line(80),
  text: line(FACT_TEXT_MAX_LENGTH).describe(FACT_TEXT_GUIDANCE),
  /** The topic note a fact belongs with; "other" when none fits. */
  topic: FactTopicSchema.optional(),
  /** "team" only when the item clearly concerns the chat's own team, not the whole company. */
  scope: z.enum(["company", "team"]).optional(),
  /** The id of the message that stated it. */
  sourceMessageId: Id,
  /** The id of a listed existing fact this one changes or contradicts. */
  replaces: z.string().trim().max(40).optional(),
  /** Decisions: who decided. */
  decidedBy: line(120).optional(),
  /** Tasks: who has to act, in words or a teammate's name. */
  owner: line(120).optional(),
  /** Tasks: when it is due, as an ISO date or datetime. */
  due: z.string().trim().max(40).optional(),
  /** Deliverables: the file name. */
  fileName: line(200).optional(),
  /** The date a figure or status held, as an ISO date: stated, or the message's date. */
  asOf: z.iso.date().optional(),
  /** Who set a goal, target, price or policy, with their role when stated. */
  setBy: line(120).optional(),
  /** The ref of a listed discovery item this item tells the team something about. */
  discovery: z.string().trim().max(10).optional(),
});
export type CapturedItem = z.infer<typeof CapturedItemSchema>;

export const DISCOVERY_KNOWN_MAX = 600;

/** A discovery item's known summary, rewritten to take in what the chat added. */
export const KnownUpdateSchema = z.object({
  discovery: z.string().trim().min(1).max(10),
  summary: line(DISCOVERY_KNOWN_MAX),
});
export type KnownUpdate = z.infer<typeof KnownUpdateSchema>;

export const CaptureExtractionSchema = z.object({
  items: z.array(CapturedItemSchema).max(CAPTURE_ITEMS_MAX),
  known: z.array(KnownUpdateSchema).max(CAPTURE_ITEMS_MAX).optional(),
});
export type CaptureExtraction = z.infer<typeof CaptureExtractionSchema>;

/** An auto-saved item as the owner sees it in a zone, with where it came from. */
export const AutoSavedItemSchema = z.object({
  id: Id,
  kind: AutoItemKindSchema,
  text: z.string(),
  asOf: z.string().nullable().default(null),
  setBy: z.string().nullable().default(null),
  botName: z.string().nullable(),
  threadId: Id.nullable(),
  sourceMessageId: z.string().nullable(),
  savedAt: IsoDate,
});
export type AutoSavedItem = z.infer<typeof AutoSavedItemSchema>;
